import { ref, Ref } from 'vue';

import axios from 'axios';
import isEmail from 'validator/lib/isEmail';
import { FormLabel, LABEL_FIELD_BETWEEN, LABEL_LOGIN, LABEL_FIELD_INVALID, LABEL_USERNAME_ALREADY_EXISTS, LABEL_USERNAME_INVALID } from './form';

interface UserNameStatusValidation {
  code?: string;
  valid: boolean;
  error?: string;
}

interface Result {
  isUserNameInvalid: Ref<boolean>;
  userNameMessage: Ref<FormLabel | null>;
  userName: Ref<string>;
  autoValidateUserName(type: 'b' | 'k' | ''): Promise<void>;
  validateUserName(serverValidation: boolean): Promise<boolean>;
}

async function apiCall(username: string, emailDomain: string, lookAndFeel: string): Promise<UserNameStatusValidation> {
  const result: UserNameStatusValidation = { valid: false };

  try {
    const axiosUserNameResponse = await axios.get('/api/v1/account/available', {
      params: {
        name: username,
        domain: emailDomain,
      },
      headers: {
        lf: lookAndFeel,
      },
      validateStatus(status) {
        return status < 500; // Resolve only if the status code is less than 500
      },
    });

    if (axiosUserNameResponse.status === 200) {
      result.valid = true;
    } else if (axiosUserNameResponse.status === 400) {
      result.valid = false;

      // console.log('Username validation:', axiosUserNameResponse.status, axiosUserNameResponse.data);
      const validationErrors = axiosUserNameResponse.data && axiosUserNameResponse.data.errors;
      if (validationErrors) {
        result.code = validationErrors[0].code;
      }
    } else {
      // Ignore other 400 errors (validated later)
      result.code = 'ERROR';
      result.valid = false;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    result.code = 'ERROR';
    result.valid = false;
    result.error = err.message;
    console.log('Unable to validate username', err);
  }

  return result;
}

/**
 *
 * @param usernameMinLength
 * @param usernameMaxLength
 * @param emailDomain
 * @param lf
 * @returns
 */

const useUserNameValidation = (usernameMinLength: number, usernameMaxLength: number, emailDomainRef: Ref<string>, lf: string): Result => {
  const isUserNameInvalid = ref(false);
  const userNameMessage = ref<FormLabel | null>(null);
  const userName = ref('');

  const userNameCache = new Map<string, UserNameStatusValidation>();
  let userNameServerValidatedResult: UserNameStatusValidation;

  const clearUserNameInvalidState = () => {
    userNameMessage.value = null;
    isUserNameInvalid.value = false;
  };

  /**
   * Server call
   */
  const serverValidation = async (username: string) => {
    let result: UserNameStatusValidation;

    // check cache
    const cachedResult = userNameCache.get(`${username}@${emailDomainRef.value}`);

    if (!cachedResult) {
      const r = await apiCall(username, emailDomainRef.value, lf);
      userNameCache.set(`${username}@${emailDomainRef.value}`, r);
      userNameServerValidatedResult = r;
      result = userNameServerValidatedResult;
    } else {
      result = cachedResult;
    }

    // clear validation message
    clearUserNameInvalidState();

    if (!result.valid) {
      isUserNameInvalid.value = true;
      if (result.code === 'ACCOUNT_ALREADY_EXISTS') {
        userNameMessage.value = { label: LABEL_USERNAME_ALREADY_EXISTS };
      } else if (result.code === 'ERROR') {
        isUserNameInvalid.value = false;
        // Communication error, rate limiter // go to next step
      } else {
        userNameMessage.value = { label: LABEL_USERNAME_INVALID };
      }
    }
  };

  /**
   * Validate user name
   */

  const validateUserName = async (serverCall: boolean): Promise<boolean> => {
    const minLength = usernameMinLength;
    const maxLength = usernameMaxLength;
    const domain = emailDomainRef.value;
    const usernameValue = userName.value;

    if (usernameValue.length < minLength) {
      isUserNameInvalid.value = true;
      userNameMessage.value = {
        label: LABEL_FIELD_BETWEEN,
        params: [{ label: LABEL_LOGIN }, minLength, maxLength],
      };
      return false;
    }

    if (!isEmail(`${usernameValue}@${domain}`)) {
      isUserNameInvalid.value = true;
      userNameMessage.value = {
        label: LABEL_FIELD_INVALID,
        params: [{ label: LABEL_LOGIN }, usernameValue],
      };
      return false;
    }

    if (serverCall) {
      // not key press validate from server
      try {
        await serverValidation(usernameValue);
      } catch (err) {
        console.log(err);
        clearUserNameInvalidState();
      }
    } else {
      clearUserNameInvalidState();
    }

    return !isUserNameInvalid.value;
  };

  /**
   * Auto validation on user input
   */
  const autoValidateUserName = async (type: string): Promise<void> => {
    const wasValid = !isUserNameInvalid.value;
    const isKeypress = type === 'k';
    const isBlur = type === 'b';
    const usernameValue = userName.value;

    if (isKeypress && wasValid) {
      // wait blur before validating
      return;
    }

    if (isBlur && usernameValue.length === 0) {
      clearUserNameInvalidState();
      return;
    }

    // only server validation on blur
    const doServerValidaation = !isKeypress;
    await validateUserName(doServerValidaation);
  };

  return {
    isUserNameInvalid,
    userNameMessage,
    userName,
    autoValidateUserName,
    validateUserName,
  };
};

export default useUserNameValidation;
